import { Alert, Button, Spinner } from "@norican/react";
import { Navigate, Outlet } from "react-router";

import { queueHref } from "@/features/queue/queue-defaults";
import { useMe } from "@/lib/api";
import { isExecutiveOnly } from "@/lib/areas";
import { logger } from "@/lib/logger";
import { UsageTracker } from "@/lib/usage-react";

/** Shown when I2Q could not say who is signed in (no connection, server error). */
export const IDENTITY_UNREACHABLE_TEXT =
  "I2Q is not reachable, so it cannot check your sign-in. You are still signed in. Check your connection and try again.";

/**
 * True only when the server said the session is missing or ended (401). A transport
 * error, a 5xx or a timeout is not a sign-out (end user review F10, 11 Oct 2026).
 */
export function isSignedOut(error: unknown): boolean {
  return (error as { status?: number } | null | undefined)?.status === 401;
}

function Waiting() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner size="md" tone="neutral" />
    </div>
  );
}

/** The page while the identity read failed for another reason than a sign-out. */
export function IdentityUnreachable({ onRetry, busy }: { onRetry: () => void; busy?: boolean }) {
  return (
    <div className="bg-bg flex min-h-screen items-center justify-center p-4">
      <Alert tone="warning" title="I2Q is not reachable right now" className="max-w-md">
        <span className="flex flex-col items-start gap-2">
          <span>{IDENTITY_UNREACHABLE_TEXT}</span>
          <Button size="sm" variant="outline" tone="neutral" loading={busy} onClick={onRetry}>
            Try again
          </Button>
        </span>
      </Alert>
    </div>
  );
}

/**
 * What a guarded route renders for the identity read: waiting, the sign-in page (401 or
 * no session), the "not reachable" page (any other failure), or null to go on.
 */
function useIdentityGate() {
  const { data: me, isLoading, error, isFetching, refetch } = useMe();
  if (isLoading) {
    return { me, gate: <Waiting /> };
  }
  if (error && !isSignedOut(error)) {
    logger.warn("[auth] identity read failed, not a sign-out", error);
    return {
      me,
      gate: <IdentityUnreachable busy={isFetching} onRetry={() => void refetch()} />,
    };
  }
  if (error || !me) {
    return { me, gate: <Navigate to="/login" replace /> };
  }
  return { me, gate: null };
}

export function ProtectedRoute() {
  const { me, gate } = useIdentityGate();
  if (gate) {
    return gate;
  }
  // The usage log listens here: on every signed-in page, never on the sign-in page.
  return (
    <>
      <UsageTracker me={me} />
      <Outlet context={{ me }} />
    </>
  );
}

export function AdminRoute() {
  const { me, gate } = useIdentityGate();
  if (gate) {
    return gate;
  }
  if (!me) {
    return <Navigate to="/login" replace />;
  }

  if (me.role !== "admin") {
    return <Navigate to="/queue" replace />;
  }

  return <Outlet context={{ me }} />;
}

/**
 * Where a signed-in person starts: the queue as it was last left (its remembered area
 * and filters), for managers too (Timo, 12 Oct 2026: "always start in the queue"; the
 * Summary with its start card is one click away). Only an executive-only session, which
 * cannot open the queue, starts on the dashboard Summary.
 */
export function landingPath(me: Parameters<typeof isExecutiveOnly>[0]): string {
  if (isExecutiveOnly(me)) {
    return "/dashboard?tab=summary";
  }
  return queueHref();
}

/** `/` and `/home` (the sign-in lands there): the queue, or the Summary for an executive. */
export function HomeRedirect() {
  const { data: me } = useMe();
  return <Navigate to={landingPath(me)} replace />;
}

/**
 * The pages of the sales work (queue, case, learned corrections, rules, feedback triage).
 * An executive-only session does no sales work and is sent to the dashboard Summary; the
 * server answers 403 to it on these routes either way.
 */
export function SalesWorkRoute() {
  const { data: me } = useMe();
  if (isExecutiveOnly(me)) {
    return <Navigate to="/dashboard?tab=summary" replace />;
  }
  return <Outlet />;
}
